// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 1 miest
// PartnerDetail / DetailPane — embeddable detail komponent.
// Exporty:
//   - DetailPane (named) — embeddable do PartneriView master-detail (BEZ breadcrumb, BEZ Späť)
//   - default = standalone PartnerDetail (s breadcrumb + Späť, ak by sa niekedy zobrazil samostatne)
//
// Tabs: Info / Štatistiky (predaj/nákup per kalendárny rok) / FA (TODO) / Audit (TODO)

import React, { useState, useEffect, useMemo } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'   // straz_v4 (29. 9. 2026): rozpísaný formulár drží spoločnú stráž odchodu (ústava 53.1.b)
import DocumentCreator from '../components/DocumentCreator.jsx'
import EmailReplyBox from '../components/EmailReplyBox.jsx'
import { normPhone } from '../utils/phone.js'
import { useParams, useNavigate } from 'react-router-dom'
import { fetchAuthJSON, fetchAuth } from '../auth/fetchAuth.js'
import SmsButton from '../components/SmsButton.jsx'
import { useBreadcrumb } from '../components/Layout.jsx'
import { Btn, BtnNav, BtnGhost } from '../components/Btn.jsx'
import { Table, THead, TBody, TR, TH, TD } from '../components/Table.jsx'
import { useToast } from '../components/Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS, SK } from '../theme.js'
import { Tabs } from '../components/Ui.jsx'
import TelefonInput from '../components/TelefonInput.jsx'
import { OsobaBunky, bunkyOsoby } from '../components/KontaktyPartnera.jsx'   // KONTAKT_BUNKY_V1 (ústava 139)
import { useConfirm } from '../components/ConfirmDialog.jsx'

const TABS = [
  { key: 'info',     label: 'Info' },
  { key: 'kontakty', label: 'Kontaktné osoby' },   // V3 131.10
  { key: 'staty',    label: 'Štatistiky' },
  { key: 'fa',       label: 'Faktúry' },
  { key: 'audit',    label: 'Audit' }
]

// ============================================================
// DetailPane — EMBEDDABLE (pre master-detail v PartneriView)
// onEdit(partner) — callback ked user klikne "✎ Upraviť" (otvori Edit modal)
// ============================================================
export function DetailPane({ id, onReload, onEdit }) {
  const [partner, setPartner] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('info')
  const [docOpen, setDocOpen] = useState(false)
  const toast = useToast()

  useEffect(() => {
    if (!id) return
    setLoading(true)
    fetchAuthJSON(`/api/customers/${id}`)
      .then(p => setPartner(p))
      .catch(e => toast.error(`Načítanie partnera ${id} zlyhalo: ${e.message}`))
      .finally(() => setLoading(false))
  }, [id, toast])

  if (loading) {
    return <div style={{ color: COLORS.muted, padding: SPACE.xl, background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, height: '100%' }}>Načítavam {id}…</div>
  }
  if (!partner) {
    return <div style={{ color: COLORS.remove, padding: SPACE.xl, background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, height: '100%' }}>Partner {id} sa nenašiel.</div>
  }

  return (
    <div style={{
      background: COLORS.bg,
      border: `1px solid ${COLORS.line}`,
      borderRadius: RADIUS.lg,
      padding: SPACE.lg,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* HLAVICKA partnera */}
      <div style={{ height: 4, background: COLORS.brand, margin: `-${SPACE.lg}px -${SPACE.lg}px ${SPACE.md}px` }} />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.md, flexWrap: 'wrap', marginBottom: SPACE.md }}>
        <h2 style={{ margin: 0, fontSize: TYPO.sizes.xl, fontWeight: TYPO.weights.extra }}>{partner.name}</h2>
        <span style={{ color: COLORS.muted, fontFamily: TYPO.mono, fontSize: TYPO.sizes.sm }}>{partner.id}</span>
        <span style={{ flex: 1 }} />
        {onEdit && (
          <BtnNav onClick={() => onEdit(partner)} title="Upraviť (alebo 2× klik v zozname / Enter)">Upraviť</BtnNav>
        )}
        <BtnNav onClick={() => setDocOpen(true)} title="Vytvoriť KAMIR listinu pre partnera">Dokument</BtnNav>
      </div>

      {docOpen && <DocumentCreator open onClose={() => setDocOpen(false)} context={{ recipient: [partner.name, partner.street || partner.adresa || partner.address, [partner.psc || partner.zip, partner.city || partner.mesto].filter(Boolean).join(' ')].filter(Boolean).join('\n') }} />}
      {partner.email && <div style={{ marginBottom: SPACE.md }}><EmailReplyBox target={{}} defaultTo={partner.email} defaultSubject={''} label="Napísať e-mail partnerovi" /></div>}
      {/* TABS */}
      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {/* CONTENT — biela karta na oranžovom paneli (ústava 61.7) */}
      <div style={{ flex: 1, overflow: 'auto', background: COLORS.bg,
        border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, padding: SPACE.md }}>
        {tab === 'info' && <InfoTab partner={partner} />}
        {tab === 'kontakty' && <KontaktyTab partner={partner} />}
        {tab === 'staty' && <StatsTab partner={partner} />}
        {tab === 'fa' && <InvoicesTab partner={partner} />}
        {tab === 'audit' && <AuditTab partner={partner} />}
      </div>
    </div>
  )
}

// ============================================================
// Standalone fallback (default export) — s breadcrumb + Späť
// Použiteľné ak by sa /partneri/:id niekedy renderovalo samostatne (mimo master-detail)
// ============================================================
export default function PartnerDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { setTrail } = useBreadcrumb()

  useEffect(() => {
    setTrail([
      { label: 'Matrix v2', onClick: () => navigate('/') },
      { label: 'Partneri', onClick: () => navigate('/partneri') },
      { label: id || '…' }
    ])
    return () => setTrail([])
  }, [id, setTrail, navigate])

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: SPACE.md }}>
        <BtnGhost onClick={() => navigate('/partneri')}>← Späť na zoznam</BtnGhost>
      </div>
      <DetailPane id={id} />
    </div>
  )
}

// ============================================================
// INFO tab
// ============================================================
function InfoTab({ partner }) {
  // p32 (16. 9. 2026): partner prepojený so subdodávkou — upravuje sa v Zamestnancoch (tam je aj v Rozpise prác)
  const navigate = useNavigate()
  const [subEmp, setSubEmp] = useState(null)
  useEffect(() => {
    setSubEmp(null)
    if (!partner.linked_employee_id) return
    fetchAuth('/api/employees/' + partner.linked_employee_id).then(r => r.ok ? r.json() : null).then(e => { if (e && e.employee_type === 'subdodavka') setSubEmp(e) }).catch(() => {})
  }, [partner.linked_employee_id])
  const rows = [
    { k: 'Typ',          v: partner.partner_type === 'dodavatel' ? 'Dodávateľ' : partner.partner_type === 'oboje' ? 'Oboje' : 'Odberateľ' },
    { k: 'IČO',          v: partner.ico || '—', mono: true },
    { k: 'DIČ',          v: partner.dic || '—', mono: true },
    { k: 'IČ DPH',       v: partner.ic_dph || '—', mono: true },
    { k: 'IBAN',         v: partner.iban || '—', mono: true },
    { k: 'Banka',        v: partner.bank || '—' },
    { k: 'Kontakt',      v: partner.contact || '—' },
    { k: 'Telefón',      v: partner.phone ? <span>{normPhone(partner.phone)} <SmsButton to={partner.phone} label="SMS" style={{ padding: '3px 11px', fontSize: 12, fontWeight: 700, border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', borderRadius: 8, marginLeft: 6 }} /></span> : '—' },
    { k: 'Email',        v: partner.email || '—' },
    { k: 'Adresa',       v: partner.address || '—' },
    { k: 'MKsoft ID',    v: partner.mksoft_firmaid || '—', mono: true },
    { k: 'Web',          v: partner.website || '—' },
    { k: 'Vytvorený',    v: partner.created_at || '—', mono: true },
    { k: 'Upravený',     v: partner.updated_at || '—', mono: true }
  ]
  return (
    <div>
      {subEmp && (
        <div style={{ marginBottom: SPACE.md, padding: SPACE.md, border: `1px solid ${COLORS.total}`, background: 'rgba(245,158,11,0.08)', borderRadius: RADIUS.md, fontSize: TYPO.sizes.sm, display: 'flex', alignItems: 'center', gap: SPACE.md, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: TYPO.weights.bold, color: COLORS.total }}>Naša subdodávka — {subEmp.name || subEmp.id}</div>
            <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>Zakladá a upravuje sa v Zamestnancoch (typ Subdodávka) — tam je parťák v Rozpise prác. Faktúry od nej sú náklad zákazky.</div>
            {/* dochadzka_v1 (Mirko 25. 9. 2026: prepínač aj na karte partnera/subdodávky; ústava 121.15) — zapisuje sa na zamestnanca (jeden register, 0.J) */}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!subEmp.attendance_required}
                onChange={async (e) => { const v = e.target.checked ? 1 : 0; try { const r = await fetchAuth('/api/employees/' + subEmp.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attendance_required: v }) }); if (r.ok) setSubEmp(await r.json()) } catch (_) {} }} />
              <span style={{ fontSize: TYPO.sizes.sm }}>Dochádzka — príchod a odchod v mobile (zapína office; bez toho subdodávka Dochádzku nevidí)</span>
            </label>
          </div>
          <BtnNav onClick={() => navigate('/zamestnanci')}>Otvoriť Zamestnancov</BtnNav>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 4 }}>
        {rows.map((r, i) => (
          <React.Fragment key={i}>
            <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm, padding: '4px 0' }}>{r.k}</div>
            <div style={{
              color: COLORS.text,
              fontFamily: r.mono ? TYPO.mono : 'inherit',
              padding: '4px 0',
              borderBottom: i < rows.length - 1 ? `1px solid ${COLORS.lineSoft}` : 'none'
            }}>{r.v}</div>
          </React.Fragment>
        ))}
      </div>
      {partner.notes && (
        <div style={{ marginTop: SPACE.md, padding: SPACE.md, background: COLORS.bgSoft, borderRadius: RADIUS.md, fontSize: TYPO.sizes.sm }}>
          <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Poznámka</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{partner.notes}</div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// KONTAKTNÉ OSOBY (V3 131.10, Mirko 15. 9. 2026: rozpočtári pri partnerovi, uložiť do budúcna)
// ============================================================
const FUNKCIE_KONTAKTU = ['rozpočtár', 'stavbyvedúci', 'konateľ', 'technik', 'fakturácia', 'obchod', 'prípravár']
function KontaktyTab({ partner }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  useDirtyFlag(!!form, { key: 'partner-kontakt', discard: () => { setForm(null) } })   // straz_v4
  const load = () => {
    setLoading(true)
    fetchAuthJSON('/api/partner-contacts?aj_odisli=1&customer_id=' + encodeURIComponent(partner.id))   // KONTAKTY_STAVBY_V2
      .then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [partner.id])   // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const save = async () => {
    const f = form || {}
    if (!String(f.first_name || '').trim() || !String(f.last_name || '').trim()) { toast.error('Zadaj meno aj priezvisko kontaktu'); return }
    if (!String(f.email || '').trim() && !String(f.telefon || '').trim()) { toast.error('Zadaj e-mail alebo telefón'); return }
    try {
      const body = JSON.stringify({ customer_id: partner.id, titul: String(f.titul || '').trim(), first_name: String(f.first_name || '').trim(), last_name: String(f.last_name || '').trim(), funkcia: f.funkcia || '', telefon: f.telefon || '', email: f.email || '', poznamka: f.poznamka || '' })
      const r = await fetchAuth(f.id ? '/api/partner-contacts/' + f.id : '/api/partner-contacts', { method: f.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || 'Uloženie zlyhalo')
      toast.success(d.duplicate ? 'Kontakt s rovnakým e-mailom alebo telefónom už pri partnerovi je' : 'Kontaktná osoba uložená')
      setForm(null); load()
    } catch (e) { toast.error(e.message) }
  }
  const archive = async (c) => {
    const ok = await confirm({ title: 'Odstrániť kontaktnú osobu?', message: [c.meno, c.funkcia, c.email, c.telefon].filter(Boolean).join(' · ') + ' — vrátiť sa dá hneď cez Späť.', danger: true, confirmLabel: 'Odstrániť' })
    if (!ok) return
    try {
      const r = await fetchAuth('/api/partner-contacts/' + c.id, { method: 'DELETE' })
      if (!r.ok) throw new Error('Odstránenie zlyhalo')
      if (toast.undo) toast.undo('Kontakt ' + (c.meno || c.email || c.telefon || '') + ' odstránený · Späť', async () => { await fetchAuth('/api/partner-contacts/' + c.id + '/restore', { method: 'PATCH' }).catch(() => {}); load() })
      load()
    } catch (e) { toast.error(e.message) }
  }
  const inp = { padding: '6px 8px', fontSize: TYPO.sizes.sm, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }
  const lab = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: TYPO.sizes.xs, color: COLORS.muted }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
        <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>Rozpočtári, stavbyvedúci a ďalší ľudia partnera. Ponúknu sa v cenovej ponuke a dopyt z ich e-mailu nájde tohto partnera.</div>
        {!form && <Btn onClick={() => setForm({ titul: '', first_name: '', last_name: '', funkcia: 'rozpočtár', telefon: '', email: '', poznamka: '' })}>+ Pridať kontakt</Btn>}
      </div>
      {form && (
        <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.md, background: COLORS.bgSoft, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: SPACE.sm }}>
          <OsobaBunky v={form} set={set} lbl={lab} inp={inp} autoFocus />
          <label style={lab}>Funkcia<input style={inp} list="funkcie-kontaktu" value={form.funkcia || ''} onChange={e => set('funkcia', e.target.value)} /></label>
          <label style={lab}>Telefón<TelefonInput style={inp} value={form.telefon || ''} onChange={v => set('telefon', v)} /></label>
          <label style={lab}>E-mail<input style={inp} type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} /></label>
          <label style={{ ...lab, gridColumn: '1 / -1' }}>Poznámka<input style={inp} value={form.poznamka || ''} onChange={e => set('poznamka', e.target.value)} placeholder="napr. rieši stavby na východe" /></label>
          <datalist id="funkcie-kontaktu">{FUNKCIE_KONTAKTU.map(x => <option key={x} value={x} />)}</datalist>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
            <BtnGhost onClick={() => setForm(null)}>Zrušiť</BtnGhost>
            <Btn onClick={save}>{form.id ? 'Uložiť zmeny' : 'Uložiť kontakt'}</Btn>
          </div>
        </div>
      )}
      {loading ? <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Načítavam…</div>
        : rows.length === 0 ? <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Partner zatiaľ nemá uložené kontaktné osoby.</div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map(c => (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1.2fr) minmax(160px, 1.4fr) minmax(120px, 1fr) auto', gap: SPACE.sm, alignItems: 'center', padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: TYPO.weights.bold, color: c.odisiel_at ? COLORS.muted : COLORS.text }}>{c.meno || '(bez mena)'}{c.odisiel_at ? <span style={{ color: '#b45309', fontWeight: 600, fontSize: TYPO.sizes.xs }}>{' · odišiel' + (c.nastupca_firma ? ' do ' + c.nastupca_firma : '') + ' ' + SK.date(String(c.odisiel_at).slice(0, 10))}</span> : null}</div>
                  <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{c.funkcia || '—'}{c.poznamka ? ' · ' + c.poznamka : ''}</div>
                </div>
                <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: TYPO.sizes.sm }}>{c.email ? <a href={'mailto:' + c.email} style={{ color: COLORS.nav }}>{c.email}</a> : <span style={{ color: COLORS.muted }}>bez e-mailu</span>}</div>
                <div style={{ fontSize: TYPO.sizes.sm, fontVariantNumeric: 'tabular-nums' }}>{c.telefon ? <a href={'tel:' + String(c.telefon).replace(/\s/g, '')} style={{ color: COLORS.text }}>{normPhone(c.telefon)}</a> : <span style={{ color: COLORS.muted }}>bez telefónu</span>}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <BtnGhost onClick={() => setForm({ ...c, ...bunkyOsoby(c) })}>Upraviť</BtnGhost>
                  <BtnGhost onClick={() => archive(c)}>Odstrániť</BtnGhost>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  )
}

// ============================================================
// STATS tab
// ============================================================
function StatsTab({ partner }) {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [bezPristupu, setBezPristupu] = useState(false)   // pravda_v1
  const toast = useToast()

  useEffect(() => {
    setLoading(true)
    // pravda_v1 (nočný čitateľ 13./14. 10. 2026): obe volania mali `r.ok ? r.json() : []`, takže
    // zamietnutie prístupu (403) sa zmenilo na prázdny zoznam a obrazovka napísala „Žiadne faktúry pre
    // tohto partnera" — aj pri partnerovi s faktúrami za stovky tisíc. V audite má Ely 61 + 58 takých
    // zamietnutí, posledné 13. 10. o 12:05. Program jej teda tri týždne tvrdil nepravdu.
    // Prístup nemeníme (to je Mirkovo rozhodnutie) — len to prestaneme zakrývať.
    setBezPristupu(false)
    const citaj = (u) => fetchAuth(u).then(r => { if (r.status === 403) { setBezPristupu(true); return [] } return r.ok ? r.json() : [] }).catch(() => [])
    Promise.all([
      citaj('/api/outgoing-invoices'),
      citaj('/api/incoming-invoices')
    ]).then(([oi, ii]) => {
      const sales = (Array.isArray(oi) ? oi : []).filter(i => i.customer_id === partner.id)
      const purchases = (Array.isArray(ii) ? ii : []).filter(i => i.partner_id === partner.id)
      const byYear = {}
      sales.forEach(i => {
        const year = (i.invoice_date || i.created_at || '').slice(0, 4) || '?'
        if (!byYear[year]) byYear[year] = { year, sales: 0, salesCount: 0, purchases: 0, purchasesCount: 0 }
        byYear[year].sales += (i.amount_total || 0)
        byYear[year].salesCount++
      })
      purchases.forEach(i => {
        const year = (i.invoice_date || i.created_at || '').slice(0, 4) || '?'
        if (!byYear[year]) byYear[year] = { year, sales: 0, salesCount: 0, purchases: 0, purchasesCount: 0 }
        byYear[year].purchases += (i.amount_total || 0)
        byYear[year].purchasesCount++
      })
      setStats(Object.values(byYear).sort((a, b) => b.year.localeCompare(a.year)))
      setLoading(false)
    }).catch(e => {
      toast.error(`Štatistiky: ${e.message}`)
      setLoading(false)
    })
  }, [partner.id, toast])

  if (loading) return <div style={{ color: COLORS.muted }}>Načítavam štatistiky…</div>
  if (!stats || stats.length === 0) {
    // pravda_v1: rozlíš „nič tu nie je" od „nemáš na to prístup" — doteraz to vyzeralo rovnako.
    return <div style={{ color: COLORS.muted, textAlign: 'center', padding: SPACE.xl }}>
      {bezPristupu
        ? 'Na faktúry nemáš prístup — tvoja rola ich nevidí. Faktúry tu môžu byť, Matrix ti ich len neukáže. Ak ich potrebuješ, povedz Mirkovi.'
        : 'Žiadne faktúry pre tohto partnera.'}
    </div>
  }

  const totalSales = stats.reduce((s, r) => s + r.sales, 0)
  const totalPurchases = stats.reduce((s, r) => s + r.purchases, 0)

  return (
    <Table>
      <THead>
        <TR>
          <TH>Rok</TH>
          <TH align="right">Predaj (€)</TH>
          <TH align="right">FA</TH>
          <TH align="right">Nákup (€)</TH>
          <TH align="right">FA</TH>
          <TH align="right">Saldo</TH>
        </TR>
      </THead>
      <TBody>
        {stats.map(r => (
          <TR key={r.year} rowKey={r.year}>
            <TD mono style={{ fontWeight: TYPO.weights.semibold }}>{r.year}</TD>
            <TD align="right" mono style={{ color: r.sales > 0 ? COLORS.nav : COLORS.muted }}>{SK.eur(r.sales, 0)}</TD>
            <TD align="right" style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>{r.salesCount}</TD>
            <TD align="right" mono style={{ color: r.purchases > 0 ? COLORS.total : COLORS.muted }}>{SK.eur(r.purchases, 0)}</TD>
            <TD align="right" style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>{r.purchasesCount}</TD>
            <TD align="right" mono style={{ color: (r.sales - r.purchases) >= 0 ? COLORS.add : COLORS.remove, fontWeight: TYPO.weights.semibold }}>
              {SK.eur(r.sales - r.purchases, 0)}
            </TD>
          </TR>
        ))}
        <TR rowKey="total" style={{ background: COLORS.totalBg, borderTop: `2px solid ${COLORS.total}` }}>
          <TD style={{ fontWeight: TYPO.weights.bold, color: COLORS.total }}>SPOLU</TD>
          <TD align="right" mono style={{ fontWeight: TYPO.weights.bold, color: COLORS.nav }}>{SK.eur(totalSales, 0)}</TD>
          <TD></TD>
          <TD align="right" mono style={{ fontWeight: TYPO.weights.bold, color: COLORS.total }}>{SK.eur(totalPurchases, 0)}</TD>
          <TD></TD>
          <TD align="right" mono style={{
            fontWeight: TYPO.weights.bold,
            color: (totalSales - totalPurchases) >= 0 ? COLORS.add : COLORS.remove
          }}>{SK.eur(totalSales - totalPurchases, 0)}</TD>
        </TR>
      </TBody>
    </Table>
  )
}

function InvoicesTab({ partner }) {
  return <div style={{ color: COLORS.muted, textAlign: 'center', padding: SPACE.xl }}>Zoznam faktúr — ROADMAP (Task #7d).</div>
}

function AuditTab({ partner }) {
  return <div style={{ color: COLORS.muted, textAlign: 'center', padding: SPACE.xl }}>Audit log — ROADMAP (Task #7e, /api/activity-log/customer/{partner.id}).</div>
}
